
import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, GraduationCap, Users, Star } from "lucide-react";
import { useClubs } from "@/hooks/useClubs";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";

interface CareerCardProps {
  title: string;
  description: string;
  image: string;
  index: number; // Added for staggered animations
}

const CareerCard: React.FC<CareerCardProps> = ({ title, description, image, index }) => {
  const { user } = useAuth();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.5,
        delay: index * 0.1,
        ease: "easeOut" 
      }}
    >
      <Card className="overflow-hidden transition-all duration-300 hover:-translate-y-2 hover:shadow-xl card-shadow">
        <div className="relative h-48 overflow-hidden">
          <img 
            src={image} 
            alt={title} 
            className="w-full h-full object-cover transition-transform duration-700 hover:scale-110" 
          />
        </div>
        <CardContent className="p-5">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-muted-foreground text-sm mt-2">{description}</p>
          <div className="mt-4 flex justify-between items-center">
            {user ? (
              <Button variant="outline" size="sm" asChild className="hover:bg-primary/10 transition-colors">
                <Link to={`/explore?career=${title.toLowerCase().replace(/\s+/g, '-')}`}>Learn More</Link>
              </Button>
            ) : (
              <Button variant="outline" size="sm" asChild className="hover:bg-primary/10 transition-colors">
                <Link to="/auth">Sign In to Learn More</Link>
              </Button>
            )}
            <motion.span 
              className="flex items-center text-muted-foreground text-xs"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
            >
              <Star className="h-3 w-3 mr-1 fill-brand-coral stroke-brand-coral" />
              <Star className="h-3 w-3 mr-1 fill-brand-coral stroke-brand-coral" />
              <Star className="h-3 w-3 mr-1 fill-brand-coral stroke-brand-coral" />
              <Star className="h-3 w-3 mr-1 fill-brand-coral stroke-brand-coral" />
              <Star className="h-3 w-3 fill-muted stroke-muted" />
            </motion.span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ExploreSection: React.FC = () => {
  const navigate = useNavigate();
  const { clubs } = useClubs();
  const { user } = useAuth();
  
  // Select just a few clubs to display in preview
  const previewClubs = clubs.slice(0, 3);
  
  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <motion.div 
      className="py-12 sm:py-16"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2 
            className="text-3xl font-bold text-foreground sm:text-4xl"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Discover Your Path
          </motion.h2>
          <motion.p 
            className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Explore careers, connect with clubs, and find opportunities aligned with your interests.
          </motion.p>
        </div>

        <Tabs defaultValue="careers" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-8 relative overflow-hidden">
            <motion.div 
              className="absolute bottom-0 h-[2px] bg-gradient-to-r from-brand-orange to-brand-teal"
              layoutId="tabHighlight"
              transition={{ type: "spring", duration: 0.6 }}
              style={{ width: "33.33%" }}
            />
            <TabsTrigger value="careers" className="flex items-center justify-center transition-all duration-300 hover:bg-primary/20">
              <Briefcase className="h-4 w-4 mr-2" />
              Careers
            </TabsTrigger>
            <TabsTrigger value="clubs" className="flex items-center justify-center transition-all duration-300 hover:bg-primary/20">
              <Users className="h-4 w-4 mr-2" />
              Clubs
            </TabsTrigger>
            <TabsTrigger value="courses" className="flex items-center justify-center transition-all duration-300 hover:bg-primary/20">
              <GraduationCap className="h-4 w-4 mr-2" />
              Courses
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="careers" className="animate-fade-in">
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <CareerCard 
                title="UX/UI Designer" 
                description="Create beautiful, intuitive interfaces for apps and websites."
                image="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                index={0}
              />
              <CareerCard 
                title="Software Developer" 
                description="Build applications and solve problems with code."
                image="https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1169&q=80"
                index={1}
              />
              <CareerCard 
                title="Environmental Scientist" 
                description="Study the environment and develop solutions for sustainability."
                image="https://images.unsplash.com/photo-1500673922987-e212871fec22?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                index={2}
              />
            </motion.div>
          </TabsContent>
          
          <TabsContent value="clubs" className="animate-fade-in">
            {user ? (
              <>
                {previewClubs.length > 0 ? (
                  <>
                    <motion.div 
                      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                      variants={containerVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {previewClubs.map((club, index) => (
                        <motion.div key={club.id} variants={itemVariants}>
                          <Card className="transition-all duration-300 hover:-translate-y-2 hover:shadow-xl card-shadow">
                            <CardContent className="p-5">
                              <div className="flex justify-between items-center">
                                <h3 className="font-semibold text-lg">{club.name}</h3>
                                <div className="bg-muted text-xs px-2 py-1 rounded-full">
                                  {club.member_count || 0} members
                                </div>
                              </div>
                              <p className="text-muted-foreground text-sm mt-2 line-clamp-3">{club.description}</p>
                              <div className="flex flex-wrap gap-2 mt-3">
                                <motion.span 
                                  className="bg-muted px-2 py-1 rounded-full text-xs transition-all duration-300 hover:bg-primary/20"
                                  whileHover={{ scale: 1.05 }}
                                >
                                  {club.category}
                                </motion.span>
                              </div>
                              <Button 
                                className="w-full mt-4 transition-all duration-300 hover:shadow-md bg-gradient-to-r from-brand-orange/90 to-brand-teal/90 hover:from-brand-orange hover:to-brand-teal" 
                                size="sm"
                                onClick={() => navigate('/clubs')}
                              >
                                Join Club
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </motion.div>
                    <motion.div 
                      className="mt-8 text-center"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    >
                      <Button onClick={() => navigate('/clubs')} variant="outline" className="transition-transform duration-300 hover:scale-105">
                        Explore All Clubs
                      </Button>
                    </motion.div>
                  </>
                ) : (
                  <motion.div 
                    className="text-center py-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <h3 className="text-xl font-medium">Loading clubs...</h3>
                    <Button onClick={() => navigate('/clubs')} variant="outline" className="mt-6 transition-transform duration-300 hover:scale-105">
                      Explore All Clubs
                    </Button>
                  </motion.div>
                )}
              </>
            ) : (
              <motion.div 
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-xl font-medium">Sign in to explore clubs</h3>
                <Button 
                  onClick={() => navigate('/auth')} 
                  variant="outline" 
                  className="mt-6 transition-transform duration-300 hover:scale-105"
                >
                  Sign In
                </Button>
              </motion.div>
            )}
          </TabsContent>
          
          <TabsContent value="courses" className="animate-fade-in">
            <motion.div 
              className="text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <h3 className="text-xl font-medium">Course exploration coming soon!</h3>
              <p className="text-muted-foreground mt-2">We're working on bringing you the best learning resources.</p>
              <Button 
                variant="outline" 
                className="mt-6 transition-transform duration-300 hover:scale-105 bg-gradient-to-r hover:from-brand-orange/20 hover:to-brand-teal/20"
              >
                Get Notified
              </Button>
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </motion.div>
  );
};

export default ExploreSection;
